import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/brand/Logo";
import { SOCIAL_LINKS } from "@/components/layout/PreFooterCTA";

const COLS = [
  { title: "Platform", links: [["Find a broker", "/brokers/finder"], ["Compare swaps", "/swaps"], ["Trading signals", "/signals"], ["Broker directory", "/brokers"]] },
  { title: "Resources", links: [["Methodology", "/legal/ranking-methodology"], ["Data methodology", "/legal/data-methodology"], ["FAQ", "/faq"], ["Contact us", "/contact"]] },
  { title: "Legal", links: [["Privacy policy", "/legal/privacy"], ["Terms of use", "/legal/terms"], ["Cookie policy", "/legal/cookies"], ["Risk disclosure", "/legal/risk"], ["Affiliate disclosure", "/legal/affiliate-disclosure"]] },
];

const Newsletter = () => {
  const [email, setEmail] = useState("");
  const submit = (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return toast.error("Enter a valid email address.");
    toast.success("You're on the list.", { description: "Broker insights and swap updates, roughly twice a month." });
    setEmail("");
  };
  return (
    <div data-testid="footer-newsletter">
      <p className="eyebrow mb-4">Newsletter</p>
      <p className="t-h6">Stay ahead of the market</p>
      <p className="mt-2 text-sm leading-relaxed text-[var(--ink-3)]">Broker insights, swap moves and new research — no noise.</p>
      <form onSubmit={submit} noValidate className="nl-field mt-5" data-testid="footer-newsletter-form">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@domain.com" aria-label="Email address" data-testid="footer-newsletter-email" />
        <button type="submit" aria-label="Subscribe" data-testid="footer-newsletter-submit"><span>Subscribe</span><ArrowRight size={14} /></button>
      </form>
    </div>
  );
};

export const Footer = () => (
  <footer className="relative overflow-hidden bg-white" data-testid="footer">
    <div className="container-x relative pt-16 lg:pt-24">
      <div className="grid grid-cols-2 gap-x-8 gap-y-12 lg:grid-cols-12">
        <div className="col-span-2 lg:col-span-3">
          <Logo />
          <p className="mt-5 max-w-[240px] text-sm leading-relaxed text-[var(--ink-3)]">Smarter broker discovery, swap intelligence and market insights for modern traders.</p>
          <p className="mt-6 eyebrow">Data as of 12 Jun 2026</p>
        </div>

        <div className="col-span-2 grid grid-cols-3 gap-6 lg:col-span-5">
          {COLS.map((c) => (
            <div key={c.title}>
              <p className="mb-5 text-[13px] font-medium tracking-[-0.01em] text-[var(--ink)]">{c.title}</p>
              <ul className="space-y-3">
                {c.links.map(([label, to]) => (
                  <li key={label}><Link to={to} className="footer-link" data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{label}</Link></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="col-span-2 lg:col-span-3"><Newsletter /></div>

        <div className="col-span-2 flex gap-2 lg:col-span-1 lg:flex-col lg:items-end" data-testid="footer-social">
          {SOCIAL_LINKS.filter((s) => s.id !== "subscribe").map(({ id, label, href, Icon }) => (
            <a key={id} href={href} target="_blank" rel="noreferrer" aria-label={label} title={label} className="footer-social" data-testid={`footer-social-${id}`}><Icon size={14} /></a>
          ))}
        </div>
      </div>

      <div className="mt-16 flex flex-col gap-4 border-t border-[var(--line)] pt-6 text-[11px] leading-relaxed text-[var(--ink-4)] lg:mt-20 lg:flex-row lg:items-start lg:justify-between">
        <p className="max-w-3xl"><strong className="font-medium text-[var(--ink-3)]">Risk warning.</strong> CFDs and leveraged forex products are complex instruments and carry a high risk of losing money rapidly. Between 68–82% of retail investor accounts lose money when trading CFDs. Trading ideas are research, not personal investment advice. All broker data on this prototype is illustrative.</p>
        <p className="shrink-0 font-mono">© 2026 ForexBrokerSignals</p>
      </div>
    </div>

    <div className="relative mt-6 h-[150px] sm:h-[220px] lg:h-[300px] select-none" aria-hidden data-testid="footer-wordmark">
      <div className="footer-glow absolute -inset-x-[10%] -bottom-1/2 h-[200%]" />
      <p className="footer-wordmark absolute inset-x-0 bottom-[-0.22em] text-center">BrokerSignals</p>
    </div>
  </footer>
);
